import React, { useCallback, useRef, useState } from 'react';

import { IInputStepSeedDress } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';

import { ModifierEvent, PickerFlyoutHost } from './pickerFlyoutRules';
import {
  buildSeedDressSelection,
  DEFAULT_SEED_DRESS_DRAFT,
  draftFromProductDefaults,
  draftFromSeedDressStep,
  draftFromStagedSeedDress,
  isSameSeedDressDraft,
} from './seedTreatmentDraft';
import { getLinkedSeedStepOptions } from './stepCalculations';
import {
  matchesByIdOrName,
  SeedDressDraft,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';

interface SeedTreatmentFlyoutArgs extends PickerFlyoutHost {
  /** The task while the Seed Treatment tab is active, otherwise undefined. */
  seedTreatmentTask?: IInputTask;
  initialSeedDressStep?: IInputStepSeedDress;
}

const matchesStep = (product: StepSelectorOption, step: IInputStepSeedDress) =>
  matchesByIdOrName(
    { id: product.globalId, name: product.item },
    { id: step.globalId, name: step.item }
  );

/**
 * Seed Treatment tab: product rows, the details flyout (add flow) and the edit two-panel.
 * - Single-select: a row opens the flyout; Add commits.
 * - Multi-select: a row click checks it with defaults (linked to the first seed for /100kg) and
 *   opens the flyout, where Add re-stages the edited values; a checked row click unchecks it.
 *   Change Details reopens the flyout with the staged values.
 * - Edit mode pre-selects the step's product and values once per session.
 */
const useSeedTreatmentFlyout = ({
  filteredOptions,
  highlightedItem,
  setHighlightedItem,
  isFlyoutOpen,
  setIsFlyoutOpen,
  selections,
  rules,
  commitSingleSelect,
  seedTreatmentTask,
  initialSeedDressStep,
}: SeedTreatmentFlyoutArgs) => {
  const [draft, setDraft] = useState<SeedDressDraft>(DEFAULT_SEED_DRESS_DRAFT);
  // Keyboard opens move focus into the flyout; mouse opens leave focus alone.
  const [isOpenedByKeyboard, setIsOpenedByKeyboard] = useState(false);
  const preselectionAppliedRef = useRef(false);

  /**
   * Edit pre-selection: highlights the step's product, loads its values and opens the two-panel,
   * once per session. StepInputSelector calls it after its open reset, like the Seed tab's.
   */
  const applyEditPreselection = useCallback(() => {
    if (!initialSeedDressStep || !seedTreatmentTask) return;
    if (preselectionAppliedRef.current || filteredOptions.length === 0) return;
    const product = filteredOptions.find((option) =>
      matchesStep(option, initialSeedDressStep)
    );
    if (!product) return;

    preselectionAppliedRef.current = true;
    setHighlightedItem(product);
    setIsFlyoutOpen(true);
    setDraft(draftFromSeedDressStep(initialSeedDressStep));
  }, [
    filteredOptions,
    initialSeedDressStep,
    seedTreatmentTask,
    setHighlightedItem,
    setIsFlyoutOpen,
  ]);

  // The edited step's values, else the staged values, else the catalogue defaults.
  const getDraftForProduct = (product: StepSelectorOption): SeedDressDraft => {
    if (initialSeedDressStep && matchesStep(product, initialSeedDressStep)) {
      return draftFromSeedDressStep(initialSeedDressStep);
    }
    const staged = selections.checkedSeedTreatments.find((checked) =>
      stepOptionsMatch(checked, product)
    );
    return staged
      ? draftFromStagedSeedDress(staged)
      : draftFromProductDefaults(product);
  };

  const close = useCallback(() => setIsFlyoutOpen(false), [setIsFlyoutOpen]);

  const reset = useCallback(() => setDraft(DEFAULT_SEED_DRESS_DRAFT), []);

  const resetPreselection = useCallback(() => {
    preselectionAppliedRef.current = false;
  }, []);

  const showFlyout = (
    product: StepSelectorOption,
    productDraft: SeedDressDraft
  ) => {
    setHighlightedItem(product);
    setIsFlyoutOpen(true);
    setDraft(productDraft);
  };

  const openForProduct = (product: StepSelectorOption) =>
    showFlyout(product, getDraftForProduct(product));

  const handleRowClick = (
    product: StepSelectorOption,
    event?: ModifierEvent
  ) => {
    if (!rules.isMultiSelectRowClick(event)) {
      openForProduct(product);
      return;
    }
    selections.lockMultiSelect();
    if (selections.isCheckedInActiveCategory(product)) {
      rules.uncheckRow(product, close);
      return;
    }
    const firstSeedStepId = seedTreatmentTask
      ? (getLinkedSeedStepOptions(seedTreatmentTask, selections.checkedSeeds)[0]
          ?.value ?? null)
      : null;
    const checkedDraft = {
      ...draftFromProductDefaults(product),
      linkedStepId: firstSeedStepId,
    };
    selections.upsertCheckedItem(
      buildSeedDressSelection(product, checkedDraft)
    );
    showFlyout(product, checkedDraft);
  };

  // detail is 0 for clicks synthesised from Enter / Space on a focused row.
  const handleRowMouseClick = (
    product: StepSelectorOption,
    event: React.MouseEvent
  ) => {
    setIsOpenedByKeyboard(event.detail === 0);
    handleRowClick(product, event);
  };

  const handleRowKeyboardActivate = (
    product: StepSelectorOption,
    event: ModifierEvent
  ) => {
    setIsOpenedByKeyboard(true);
    handleRowClick(product, event);
  };

  const handleChangeDetails = (product: StepSelectorOption) => {
    setIsOpenedByKeyboard(false);
    openForProduct(product);
  };

  const handleDraftChange = useCallback(
    (changes: Partial<SeedDressDraft>) =>
      setDraft((previous) => ({ ...previous, ...changes })),
    []
  );

  const handleAdd = (event?: ModifierEvent) => {
    if (!highlightedItem) return;
    const selection = buildSeedDressSelection(highlightedItem, draft);
    if (!rules.isMultiSelectFlyoutClick(event)) {
      commitSingleSelect(selection);
      return;
    }
    selections.lockMultiSelect();
    selections.upsertCheckedItem(selection);
    close();
  };

  const handleSave = () => {
    if (highlightedItem) {
      commitSingleSelect(buildSeedDressSelection(highlightedItem, draft));
    }
  };

  // Multi-select only: the open flyout holds values that Save would drop (not yet Added).
  const stagedProduct =
    highlightedItem &&
    selections.checkedSeedTreatments.find((checked) =>
      stepOptionsMatch(checked, highlightedItem)
    );
  const hasUnconfirmedChanges =
    !!seedTreatmentTask &&
    isFlyoutOpen &&
    selections.isMultiSelectMode &&
    !!highlightedItem &&
    (!stagedProduct ||
      !isSameSeedDressDraft(draft, draftFromStagedSeedDress(stagedProduct)));

  return {
    draft,
    hasUnconfirmedChanges,
    isOpenedByKeyboard,
    close,
    reset,
    resetPreselection,
    applyEditPreselection,
    openForProduct,
    handleRowMouseClick,
    handleRowKeyboardActivate,
    handleChangeDetails,
    handleDraftChange,
    handleAdd,
    handleSave,
  };
};

export default useSeedTreatmentFlyout;
